import React, { useState, useEffect } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Users, Trash2 } from 'lucide-react';

const ROLES = [
  'Director', 'Project Director', 'Project Manager',
  'Contracts Manager', 'Commercial Manager', 'Quantity Surveyor', 'Planner',
  'Construction Manager', 'Site Agent', 'Site Manager', 'Site Engineer', 'Foreman',
  'Procurement Manager', 'Plant Manager', 'Stores Manager',
  'Document Controller', 'Quality Manager', 'Safety Officer',
  'Project Administrator',
  'Principal Agent', 'Architect', 'Architectural Technologist',
  'Civil Engineer', 'Structural Engineer', 'Electrical Engineer',
  'Mechanical Engineer', 'Wet Services Engineer', 'Fire Engineer',
  'Electronic / ICT Engineer', 'Interior Designer', 'Landscape Architect',
  'Town Planner', 'Traffic Engineer', 'Geotechnical Engineer',
  'Environmental Consultant', 'Health and Safety Consultant',
  'Clerk of Works', 'Other'
];

const DISCIPLINES = [
  'Architectural', 'Structural', 'MEP', 'Civil', 
  'Contractor', 'Client', 'Consultant', 'Other'
];

const RELATIONSHIP_OPTIONS = ['hot', 'warm', 'neutral', 'cold'];

const ContactModal = ({ open, contact, companies, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    company_id: '',
    role: '',
    custom_role: '',
    discipline: '',
    email: '',
    phone: '',
    mobile: '',
    linkedin: '',
    relationship_strength: 'neutral',
    is_decision_maker: false,
    notes: '',
    tags: [],
  });
  const [loading, setLoading] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    if (contact) {
      setFormData({
        first_name: contact.first_name || '',
        last_name: contact.last_name || '',
        company_id: contact.company_id || '',
        role: contact.role || '',
        custom_role: contact.custom_role || '',
        discipline: contact.discipline || '',
        email: contact.email || '',
        phone: contact.phone || '',
        mobile: contact.mobile || '',
        linkedin: contact.linkedin || '',
        relationship_strength: contact.relationship_strength || 'neutral',
        is_decision_maker: contact.is_decision_maker || false,
        notes: contact.notes || '',
        tags: contact.tags || [],
      });
    } else {
      setFormData({
        first_name: '',
        last_name: '',
        company_id: '',
        role: '',
        custom_role: '',
        discipline: '',
        email: '',
        phone: '',
        mobile: '',
        linkedin: '',
        relationship_strength: 'neutral',
        is_decision_maker: false,
        notes: '',
        tags: [],
      });
    }
  }, [contact, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error('First and last name are required');
      return;
    }

    setLoading(true);
    try {
      const dataToSend = { ...formData };
      if (!dataToSend.company_id) delete dataToSend.company_id;
      if (!dataToSend.role) delete dataToSend.role;
      if (!dataToSend.discipline) delete dataToSend.discipline;

      if (contact?.id) {
        await api.crm.updateContact(contact.id, dataToSend);
        toast.success('Contact updated successfully');
      } else {
        await api.crm.createContact(dataToSend);
        toast.success('Contact created successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save contact');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!contact?.id) return;
    if (!window.confirm('Are you sure you want to delete this contact?')) return;

    setLoading(true);
    try {
      await api.crm.deleteContact(contact.id);
      toast.success('Contact deleted');
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to delete contact');
    } finally {
      setLoading(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-600" />
            {contact ? 'Edit Contact' : 'New Contact'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>First Name *</Label>
              <Input
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                placeholder="John"
                data-testid="contact-first-name-input"
              />
            </div>

            <div>
              <Label>Last Name *</Label>
              <Input
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                placeholder="Smith"
                data-testid="contact-last-name-input"
              />
            </div>

            <div className="col-span-2">
              <Label>Company</Label>
              <Select
                value={formData.company_id || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, company_id: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No company</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Role</Label>
              <Select
                value={formData.role || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, role: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {ROLES.map(r => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Custom Role</Label>
              <Input
                value={formData.custom_role}
                onChange={(e) => setFormData({ ...formData, custom_role: e.target.value })}
                placeholder="If role is Other, specify..."
              />
            </div>

            <div>
              <Label>Discipline</Label>
              <Select
                value={formData.discipline || '__none__'}
                onValueChange={(value) => setFormData({ ...formData, discipline: value === '__none__' ? '' : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select discipline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Not specified</SelectItem>
                  {DISCIPLINES.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Relationship Strength</Label>
              <Select
                value={formData.relationship_strength}
                onValueChange={(value) => setFormData({ ...formData, relationship_strength: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map(opt => (
                    <SelectItem key={opt} value={opt}>{opt.charAt(0).toUpperCase() + opt.slice(1)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="john.smith@company.com"
              />
            </div>

            <div>
              <Label>Phone</Label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="+27 11 123 4567"
              />
            </div>

            <div>
              <Label>Mobile</Label>
              <Input
                value={formData.mobile}
                onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                placeholder="+27 82 123 4567"
              />
            </div>

            <div>
              <Label>LinkedIn Profile</Label>
              <Input
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                placeholder="https://linkedin.com/in/..."
              />
            </div>

            <div className="col-span-2 flex items-center space-x-2">
              <Checkbox
                id="is_decision_maker"
                checked={formData.is_decision_maker}
                onCheckedChange={(checked) => setFormData({ ...formData, is_decision_maker: checked })}
              />
              <label htmlFor="is_decision_maker" className="text-sm font-medium leading-none cursor-pointer">
                This contact is a Decision Maker
              </label>
            </div>

            <div className="col-span-2">
              <Label>Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes about this contact..."
                rows={3}
              />
            </div>

            <div className="col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map(tag => (
                  <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded text-sm">
                    {tag}
                    <button type="button" onClick={() => removeTag(tag)} className="text-slate-400 hover:text-slate-600">&times;</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter className="flex justify-between">
            <div>
              {contact?.id && (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading}>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
              <Button type="submit" disabled={loading} data-testid="save-contact-btn">
                {loading ? 'Saving...' : (contact ? 'Update' : 'Create')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
